// src/utils/lyrics.js

/**
 * Parse plain text lyrics into stanzas + optional chorus.
 * - Blocks split by blank lines
 * - A block whose first line starts with "Chorus" or "Refrain" is the chorus
 * - Everything else is a stanza
 * Output here is a RENDER shape (array of arrays), not Firestore-safe.
 */
export function parsePlainTextToStructured(lyricsText = "") {
  const text = (lyricsText || "").replace(/\r\n/g, "\n").trim();
  if (!text) return { stanzas: [] };

  const blocks = text
    .split(/\n{2,}/g)
    .map((b) =>
      b
        .split("\n")
        .map((l) => l.replace(/\s+$/g, ""))
        .filter((l) => l.trim() !== "")
    )
    .filter((arr) => arr.length > 0);

  const isRefrainLabel = (line) => /^ *(chorus|refrain)\b/i.test(line || "");

  let chorus = null;
  const stanzas = [];

  blocks.forEach((lines) => {
    if (lines.length > 0 && isRefrainLabel(lines[0])) {
      // If first line is exactly "Chorus" or "Refrain", drop it. Otherwise keep it.
      const first = lines[0].trim().toLowerCase();
      const body =
        first === "chorus" || first === "refrain" ? lines.slice(1) : lines;
      if (!chorus) chorus = body;
      else stanzas.push(lines); // safety for repeated tagged blocks
    } else {
      stanzas.push(lines);
    }
  });

  return { stanzas, refrain: chorus && chorus.length ? chorus : undefined };
}

/**
 * Convert a RENDER shape ({stanzas: string[][], refrain?: string[]})
 * into a Firestore-safe shape (no array-of-arrays at top level).
 *
 * Firestore-safe shape example:
 * {
 *   stanzas: [{ lines: ["line1","line2"] }, { lines: [...] }],
 *   chorus?: ["line1","line2"]
 * }
 */
export function toFirestoreLyrics(structured = {}) {
  const out = { stanzas: [] };

  const st = Array.isArray(structured.stanzas) ? structured.stanzas : [];
  out.stanzas = st.map((s) => {
    const lines = Array.isArray(s) ? s : [String(s ?? "")];
    return { lines: lines.map((ln) => String(ln)) };
  });

  const chorus = structured.refrain || structured.chorus;
  if (Array.isArray(chorus)) {
    out.chorus = chorus.map((ln) => String(ln));
  } else if (typeof chorus === "string" && chorus.trim()) {
    out.chorus = [chorus.trim()];
  }

  return out;
}

/**
 * Normalize whatever is stored on the document into a RENDER shape:
 * { stanzas: string[][], refrain?: string[] }
 *
 * Supports:
 * - Legacy shape: { stanzas: string[][], refrain?: string[] }
 * - New FS-safe:  { stanzas: [{lines: string[]}...], chorus?: string[] }
 * - Fallback to parsing plain lyrics if no structured found
 */
export function ensureStructuredFromDoc(hymnDoc) {
  const ls = hymnDoc?.lyrics_structured;
  if (ls) {
    // New Firestore-safe: stanzas as objects with {lines:[]}, chorus:[]
    if (Array.isArray(ls.stanzas) && ls.stanzas.every((s) => typeof s === "object" && s !== null)) {
      const stanzas = ls.stanzas.map((s) =>
        Array.isArray(s.lines) ? s.lines.map((ln) => String(ln)) : []
      );
      const chorus = Array.isArray(ls.chorus)
        ? ls.chorus.map((ln) => String(ln))
        : undefined;
      return { stanzas, refrain: chorus };
    }

    // Legacy: stanzas as string[][], refrain:[]
    if (Array.isArray(ls.stanzas) && ls.stanzas.every((s) => Array.isArray(s))) {
      const stanzas = ls.stanzas.map((s) => s.map((ln) => String(ln)));
      const chorus =
        Array.isArray(ls.refrain) ? ls.refrain.map((ln) => String(ln)) : undefined;
      return { stanzas, refrain: chorus };
    }
  }

  // Fallback: compute from plain text
  return parsePlainTextToStructured(hymnDoc?.lyrics || "");
}
